import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, Trash2, X, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import OptimizedImage from './OptimizedImage';

const CartIcon = () => {
  const { cartItems, totalItems, totalPrice, updateQuantity, removeFromCart } = useCart();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsDrawerOpen(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawerOpen]);

  const badgeVariants = {
    visible: { 
      scale: [1, 1.2, 1],
      y: [0, -4, 0],
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  return (
    <div className="relative">
      <motion.button
        type="button"
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsDrawerOpen(true)}
        aria-label={`Open cart${totalItems ? `, ${totalItems} items` : ''}`}
        data-cart-target
        className="relative flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/80 transition hover:border-[#f5c400]/70 hover:text-[#f5c400]"
      >
        <ShoppingCart size={17} />
        {totalItems > 0 && (
          <motion.span
            key={`impact-${totalItems}`}
            aria-hidden="true"
            initial={{ opacity: 0.8, scale: 0.72 }}
            animate={{ opacity: 0, scale: 1.65 }}
            transition={{ duration: 0.42, delay: 0.62, ease: 'easeOut' }}
            className="pointer-events-none absolute inset-0 rounded-full border border-[#f5c400]"
          />
        )}
        {totalItems > 0 && (
          <motion.span
            key={totalItems}
            className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#f5c400] px-1 text-[10px] font-black text-black"
            variants={badgeVariants}
            initial="visible"
            animate="visible"
          >
            {totalItems > 99 ? '99+' : totalItems}
          </motion.span>
        )}
      </motion.button>

      {createPortal(
        <AnimatePresence>
          {isDrawerOpen && (
            <>
            <motion.button
              type="button"
              aria-label="Close cart drawer"
              onClick={() => setIsDrawerOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="cab-cart-backdrop fixed inset-0 z-[60] cursor-default"
            />
            <motion.aside
              role="dialog"
              aria-modal="true"
              aria-labelledby="cart-drawer-title"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 280 }}
              className="cab-cart-modal fixed z-[70] mx-auto flex w-full max-w-2xl flex-col text-white"
            >
              <div className="cab-cart-grip" />
              <div className="cab-cart-header flex items-start justify-between px-5 py-5 sm:px-8">
                <div>
                  <div className="cab-cart-eyebrow">Your CAB collection <span>· CAB STORE</span></div>
                  <div className="mt-2 flex items-center gap-3">
                    <h2 id="cart-drawer-title" className="cab-cart-title">Shopping bag</h2>
                    <span className="cab-cart-count">{totalItems}</span>
                  </div>
                  <p className="cab-cart-subtitle">Review your pieces before checkout.</p>
                </div>
                <button type="button" onClick={() => setIsDrawerOpen(false)} aria-label="Close cart" className="cab-cart-close">
                  <X size={18} />
                </button>
              </div>

              <div className="cab-cart-body min-h-0 overflow-y-auto px-5 py-5 sm:px-8">
                {cartItems.length === 0 ? (
                  <div className="cab-cart-empty flex flex-col items-center justify-center text-center">
                    <div className="cab-cart-empty-icon"><ShoppingCart size={30} /></div>
                    <p className="cab-cart-empty-title">Your cart is empty</p>
                    <p className="cab-cart-empty-copy">Your next CAB essential is waiting.</p>
                    <Link to="/shop" onClick={() => setIsDrawerOpen(false)} className="cab-cart-cta mt-6">Continue shopping <ArrowRight size={16} /></Link>
                  </div>
                ) : (
                  <div className="cab-cart-items space-y-3">
                    {cartItems.map((item) => (
                      <div key={item.id} className="cab-cart-item flex gap-3 p-3 sm:gap-4 sm:p-4">
                        <OptimizedImage src={item.image || '/assets/home 1.png'} alt={item.name} className="cab-cart-item-image h-20 w-16 object-cover sm:h-24 sm:w-20" sizes="80px" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="cab-cart-item-label">Official CAB</div>
                              <h3 className="cab-cart-item-name mt-1 truncate">{item.name}</h3>
                              <p className="cab-cart-item-detail">{item.price} DT each {item.size ? `· Size ${item.size}` : ''}</p>
                            </div>
                            <strong className="cab-cart-item-price whitespace-nowrap">{item.price * item.quantity} DT</strong>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="cab-cart-quantity flex items-center gap-2 p-1">
                              <button type="button" aria-label={`Decrease ${item.name} quantity`} onClick={() => updateQuantity(item.id, item.quantity - 1)} className="cab-cart-quantity-button"><Minus size={13} /></button>
                              <span className="min-w-6 text-center text-sm font-bold">{item.quantity}</span>
                              <button type="button" aria-label={`Increase ${item.name} quantity`} onClick={() => updateQuantity(item.id, item.quantity + 1)} className="cab-cart-quantity-button"><Plus size={13} /></button>
                            </div>
                            <button type="button" aria-label={`Remove ${item.name}`} onClick={() => removeFromCart(item.id)} className="cab-cart-remove"><Trash2 size={13} /> Remove</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {cartItems.length > 0 && (
                <div className="cab-cart-summary grid gap-4 px-5 py-5 sm:grid-cols-[1fr_auto] sm:px-8">
                  <div className="flex items-center justify-between sm:block">
                    <div className="cab-cart-summary-label">Subtotal <span>· Delivery calculated at checkout</span></div>
                    <strong className="cab-cart-total ml-4">{totalPrice} DT</strong>
                  </div>
                  <div className="flex gap-2 sm:justify-end">
                    <button type="button" onClick={() => setIsDrawerOpen(false)} className="cab-cart-secondary min-h-11 flex-1 px-4 sm:flex-none">Continue shopping</button>
                    <Link to="/cart" onClick={() => setIsDrawerOpen(false)} className="cab-cart-cta min-h-11 flex-1 px-4 sm:flex-none">Proceed to checkout <ArrowRight size={15} /></Link>
                  </div>
                </div>
              )}
            </motion.aside>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default CartIcon;
